const clamp = (value, low, high) => Math.max(low, Math.min(high, value));

/** Cancellable presentation phases driven exclusively by the player's visible
 * frame delta in milliseconds. Preparation is a real resource promise; Skip
 * only changes performances and can never mark a pending resource ready.
 * This owner never advances physics, changes awards or starts another clock. */
export function createBobSequences({ onChange = () => {} } = {}) {
  let generation = 0,
    phase = 'idle',
    elapsed = 0,
    prepared = false;
  let assets = null,
    skipped = false,
    introMs = 1800,
    rewardMs = 900,
    reduced = false;
  function enter(next) {
    phase = next;
    elapsed = 0;
  }
  function start(preparation, options = {}) {
    const epoch = ++generation;
    reduced = Boolean(options.reduced);
    skipped = prepared = false;
    assets = null;
    introMs = 1800;
    rewardMs = 900;
    // Injectable renderer doubles without asynchronous assets retain the
    // established direct controller contract; the production renderer prepares.
    if (!preparation) {
      prepared = true;
      enter('playing');
      return;
    }
    enter(options.next ? 'next' : 'preparing');
    Promise.resolve(preparation).then(
      (result) => {
        if (epoch !== generation) return;
        prepared = true;
        assets = result;
        introMs = clamp((result?.durations?.intro || 1.8) * 1000, 500, 3200);
        rewardMs = clamp((result?.durations?.reward || 0.9) * 1000, 300, 2000);
        onChange();
      },
      () => {
        if (epoch !== generation) return;
        // Renderer preparation owns resource fallbacks. An unexpected rejection
        // remains an explicit failure; it is never treated as a ready world.
        enter('error');
        onChange();
      },
    );
  }
  function update(dt, prefersReduced = reduced) {
    reduced = Boolean(prefersReduced);
    elapsed += clamp(Number(dt) || 0, 0, 50);
    let go = false;
    if (phase === 'next' && elapsed >= (reduced ? 0 : 250)) enter('preparing');
    if (phase === 'preparing' && prepared) enter(skipped || reduced ? 'ready' : 'intro');
    if (phase === 'intro' && (skipped || reduced || elapsed >= introMs)) enter('ready');
    const fallback =
      assets && ['character', 'crate', 'backdrop'].some((key) => assets[key] === false);
    if (phase === 'ready' && elapsed >= (fallback ? 1400 : reduced ? 100 : 300)) enter('countdown');
    if (phase === 'countdown' && elapsed >= (reduced ? 300 : 1500)) {
      enter('playing');
      go = true;
    }
    if (phase === 'end' && elapsed >= (reduced ? 0 : 1200)) enter('reward');
    if (phase === 'reward' && elapsed >= (reduced ? 0 : rewardMs)) enter('results');
    return { go, ...snapshot() };
  }
  function snapshot() {
    const duration =
      phase === 'intro'
        ? introMs
        : phase === 'countdown'
          ? reduced
            ? 300
            : 1500
          : phase === 'end'
            ? 1200
            : phase === 'reward'
              ? rewardMs
              : 300;
    return {
      phase,
      elapsed,
      prepared,
      assets,
      skipped,
      active: ['next', 'preparing', 'intro', 'ready', 'countdown', 'end', 'reward'].includes(phase),
      presentation: { phase, seconds: elapsed / 1000, duration: duration / 1000 },
      countdown: phase === 'countdown' ? Math.max(1, 3 - Math.floor(elapsed / 500)) : null,
    };
  }
  return {
    start,
    update,
    snapshot,
    finish(success, prefersReduced) {
      reduced = Boolean(prefersReduced);
      enter(success ? 'end' : 'results');
    },
    skip() {
      skipped = true;
      if (phase === 'intro') enter('ready');
      if (phase === 'end' || phase === 'reward') enter('results');
    },
    cancel() {
      generation++;
      prepared = false;
      assets = null;
      enter('idle');
    },
  };
}
